import React, { useEffect, useState, useCallback } from 'react';
import { Card, Button, Form, Row, Col, Badge, InputGroup, Modal, Table, Alert, Spinner } from 'react-bootstrap';
import { useNavigate, Link } from 'react-router-dom';
import KbAssistantMarkdown from '../components/KbAssistantMarkdown';
import {
  KnowledgeArticle,
  KnowledgeArticleCategory,
  KnowledgeAuthorCorrelationResponse,
  KnowledgeCategoriesImportPayload,
  KnowledgeCategoriesImportResponse,
  KnowledgeGlobalKbEditorEntry,
  User,
  KnowledgeBaseReferenceItem,
} from '../types';
import { knowledgeBaseApi, usersApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useModal } from '../context/ModalContext';
import './Dashboard.css';
import './KnowledgeBase.css';

const KnowledgeBase: React.FC = () => {
  const [articles, setArticles] = useState<KnowledgeArticle[]>([]);
  const [categories, setCategories] = useState<KnowledgeArticleCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [authorFilter, setAuthorFilter] = useState('');
  const [authorOptions, setAuthorOptions] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState<'updated_at' | 'created_at' | 'title' | 'author'>('updated_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(24);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState<number | ''>('');
  const [favoritesOnly, setFavoritesOnly] = useState(false);
  const [showCategoriesModal, setShowCategoriesModal] = useState(false);
  const [showImportCategoriesModal, setShowImportCategoriesModal] = useState(false);
  const [showGlobalEditorsModal, setShowGlobalEditorsModal] = useState(false);
  const [globalEditors, setGlobalEditors] = useState<KnowledgeGlobalKbEditorEntry[]>([]);
  const [loadingGlobalEditors, setLoadingGlobalEditors] = useState(false);
  const [globalUserSearch, setGlobalUserSearch] = useState('');
  const [globalUserSearchLoading, setGlobalUserSearchLoading] = useState(false);
  const [globalUserResults, setGlobalUserResults] = useState<User[]>([]);
  const [showCorrelateModal, setShowCorrelateModal] = useState(false);
  const [correlateLoading, setCorrelateLoading] = useState(false);
  const [correlateResult, setCorrelateResult] = useState<KnowledgeAuthorCorrelationResponse | null>(null);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryDescription, setNewCategoryDescription] = useState('');
  const [creatingCategory, setCreatingCategory] = useState(false);
  const [askQuestion, setAskQuestion] = useState('');
  const [askAnswer, setAskAnswer] = useState<string | null>(null);
  const [askReferences, setAskReferences] = useState<KnowledgeBaseReferenceItem[]>([]);
  const [askLoading, setAskLoading] = useState(false);
  const [kbAssistantVisibleForGeneral, setKbAssistantVisibleForGeneral] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showAlert, showConfirm } = useModal();
  const isAdmin = user?.role === 'admin';
  const showKbAssistant = Boolean(isAdmin || kbAssistantVisibleForGeneral);

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    const loadAssistantVisibility = async () => {
      try {
        const visibility = await knowledgeBaseApi.getAssistantVisibility();
        setKbAssistantVisibleForGeneral(Boolean(visibility.assistant_visible_for_general));
      } catch (error) {
        console.error('Erro ao carregar visibilidade do assistente da KB:', error);
      }
    };
    loadAssistantVisibility();
  }, []);

  useEffect(() => {
    fetchArticles();
  }, [searchText, authorFilter, selectedCategory, favoritesOnly, sortBy, sortOrder, page, pageSize]);

  useEffect(() => {
    setPage(1);
  }, [searchText, authorFilter, selectedCategory, favoritesOnly, sortBy, sortOrder, pageSize]);

  useEffect(() => {
    const timer = window.setTimeout(async () => {
      try {
        const options = await knowledgeBaseApi.getArticleAuthors({
          search: authorFilter,
          limit: 30,
        });
        setAuthorOptions(options);
      } catch (error) {
        // Não interrompe fluxo principal caso autocomplete falhe.
        console.error('Erro ao carregar opções de autor:', error);
      }
    }, 250);

    return () => window.clearTimeout(timer);
  }, [authorFilter]);

  const loadGlobalEditors = useCallback(async () => {
    try {
      setLoadingGlobalEditors(true);
      const data = await knowledgeBaseApi.listGlobalKbEditors();
      setGlobalEditors(data);
    } catch (error: any) {
      console.error(error);
      showAlert({ title: 'Erro', message: 'Erro ao carregar editores globais da KB.', variant: 'danger' });
    } finally {
      setLoadingGlobalEditors(false);
    }
  }, [showAlert]);

  useEffect(() => {
    if (showGlobalEditorsModal) {
      loadGlobalEditors();
    }
  }, [showGlobalEditorsModal, loadGlobalEditors]);

  const handleSearchGlobalUsers = async () => {
    const q = globalUserSearch.trim();
    if (q.length < 2) {
      showAlert({ title: 'Aviso', message: 'Digite pelo menos 2 caracteres.', variant: 'warning' });
      return;
    }
    try {
      setGlobalUserSearchLoading(true);
      const res = await usersApi.search({ search: q, limit: 15 });
      setGlobalUserResults(res.items);
    } catch (error: any) {
      console.error(error);
      showAlert({ title: 'Erro', message: 'Erro ao buscar usuários.', variant: 'danger' });
    } finally {
      setGlobalUserSearchLoading(false);
    }
  };

  const handleGrantGlobalEditor = async (userId: number) => {
    try {
      await knowledgeBaseApi.grantGlobalKbEditor(userId);
      showAlert({ title: 'Sucesso', message: 'Usuário pode editar todos os artigos da KB.', variant: 'success' });
      await loadGlobalEditors();
      setGlobalUserResults([]);
    } catch (error: any) {
      console.error(error);
      showAlert({
        title: 'Erro',
        message: error.response?.data?.detail || 'Erro ao conceder permissão global.',
        variant: 'danger',
      });
    }
  };

  const handleRevokeGlobalEditor = async (userId: number) => {
    const ok = await showConfirm({
      title: 'Revogar edição global',
      message: 'Este usuário deixará de poder editar todos os artigos da base de conhecimento.',
      variant: 'warning',
      confirmLabel: 'Revogar',
      cancelLabel: 'Cancelar',
    });
    if (!ok) return;
    try {
      await knowledgeBaseApi.revokeGlobalKbEditor(userId);
      showAlert({ title: 'Sucesso', message: 'Permissão global revogada.', variant: 'success' });
      await loadGlobalEditors();
    } catch (error: any) {
      console.error(error);
      showAlert({ title: 'Erro', message: 'Erro ao revogar.', variant: 'danger' });
    }
  };

  const handleCorrelatePreview = async () => {
    try {
      setCorrelateLoading(true);
      setCorrelateResult(null);
      const res = await knowledgeBaseApi.correlateAuthors(false);
      setCorrelateResult(res);
    } catch (error: any) {
      console.error(error);
      showAlert({
        title: 'Erro',
        message:
          error.code === 'ECONNABORTED'
            ? 'A correlação demorou mais que o esperado. Tente novamente; aumentamos o tempo limite nesta versão.'
            : error.response?.data?.detail || 'Erro ao correlacionar autores.',
        variant: 'danger',
      });
    } finally {
      setCorrelateLoading(false);
    }
  };

  const handleCorrelateApply = async () => {
    const ok = await showConfirm({
      title: 'Aplicar correlação',
      message:
        'Será atualizado o campo "dono" (created_by) apenas nos artigos com correspondência de alta confiança entre o nome importado e um usuário ativo. Deseja continuar?',
      variant: 'warning',
      confirmLabel: 'Aplicar',
      cancelLabel: 'Cancelar',
    });
    if (!ok) return;
    try {
      setCorrelateLoading(true);
      const res = await knowledgeBaseApi.correlateAuthors(true);
      setCorrelateResult(res);
      showAlert({
        title: 'Concluído',
        message: `Atualizados ${res.updated_count} artigo(s). Revise a lista "precisa revisão" no relatório.`,
        variant: 'success',
      });
      fetchArticles();
    } catch (error: any) {
      console.error(error);
      showAlert({
        title: 'Erro',
        message:
          error.code === 'ECONNABORTED'
            ? 'A aplicação da correlação demorou mais que o esperado. Tente novamente.'
            : error.response?.data?.detail || 'Erro ao aplicar correlação.',
        variant: 'danger',
      });
    } finally {
      setCorrelateLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const data = await knowledgeBaseApi.getCategories();
      setCategories(data);
    } catch (error: any) {
      console.error('Erro ao carregar categorias:', error);
      showAlert({ title: 'Erro', message: 'Erro ao carregar categorias', variant: 'danger' });
    }
  };

  const fetchArticles = useCallback(async () => {
    try {
      setLoading(true);
      const params: any = {
        skip: (page - 1) * pageSize,
        limit: pageSize,
      };
      const countParams: any = {};
      if (selectedCategory) {
        params.category_id = selectedCategory;
        countParams.category_id = selectedCategory;
      }
      if (searchText) {
        params.search = searchText;
        countParams.search = searchText;
      }
      if (authorFilter) {
        params.author = authorFilter;
        countParams.author = authorFilter;
      }
      params.sort_by = sortBy;
      params.sort_order = sortOrder;
      if (favoritesOnly) {
        params.favorites_only = true;
        countParams.favorites_only = true;
      }
      const [data, countResult] = await Promise.all([
        knowledgeBaseApi.getArticles(params),
        knowledgeBaseApi.getArticlesCount(countParams),
      ]);
      setArticles(data);
      setTotalCount(countResult.total || 0);
    } catch (error: any) {
      console.error('Erro ao carregar artigos:', error);
      showAlert({ title: 'Erro', message: 'Erro ao carregar artigos', variant: 'danger' });
    } finally {
      setLoading(false);
    }
  }, [searchText, authorFilter, selectedCategory, favoritesOnly, sortBy, sortOrder, page, pageSize, showAlert]);

  const handleToggleFavorite = async (articleId: number) => {
    try {
      await knowledgeBaseApi.toggleFavorite(articleId);
      // Atualizar lista
      fetchArticles();
    } catch (error: any) {
      console.error('Erro ao favoritar artigo:', error);
      showAlert({ title: 'Erro', message: 'Erro ao favoritar artigo', variant: 'danger' });
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const stripHtml = (html: string) => {
    const tmp = document.createElement('DIV');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  const truncateText = (text: string, maxLength: number = 150) => {
    const plainText = stripHtml(text);
    if (plainText.length <= maxLength) return plainText;
    return plainText.substring(0, maxLength) + '...';
  };

  const handleAskKnowledgeBase = async (e: React.FormEvent) => {
    e.preventDefault();
    const q = askQuestion.trim();
    if (!q) {
      showAlert({ title: 'Aviso', message: 'Digite uma pergunta.', variant: 'warning' });
      return;
    }
    setAskLoading(true);
    try {
      const data = await knowledgeBaseApi.ask(q);
      const text =
        typeof data.answer === 'string' ? data.answer : data.answer != null ? String(data.answer) : '';
      setAskAnswer(text);
      setAskReferences(data.references || []);
    } catch (error: any) {
      setAskAnswer(null);
      setAskReferences([]);
      const detail = error.response?.data?.detail;
      const msg =
        typeof detail === 'string'
          ? detail
          : Array.isArray(detail)
            ? detail.map((d: { msg?: string }) => d?.msg || '').filter(Boolean).join(' ')
            : error.message || 'Erro ao consultar a base.';
      showAlert({
        title: 'Erro',
        message: msg || 'Erro ao consultar a base.',
        variant: 'danger',
      });
    } finally {
      setAskLoading(false);
    }
  };

  const handleAskTextareaKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key !== 'Enter' || e.shiftKey) return;
    e.preventDefault();
    if (askLoading) return;
    const form = e.currentTarget.form;
    form?.requestSubmit();
  };

  const handleCreateCategory = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newCategoryName.trim()) {
      showAlert({ title: 'Aviso', message: 'Digite o nome da categoria', variant: 'warning' });
      return;
    }

    try {
      setCreatingCategory(true);
      await knowledgeBaseApi.createCategory({
        name: newCategoryName.trim(),
        description: newCategoryDescription.trim() || undefined,
      });
      showAlert({ title: 'Sucesso', message: 'Categoria criada com sucesso', variant: 'success' });
      setNewCategoryName('');
      setNewCategoryDescription('');
      fetchCategories();
    } catch (error: any) {
      console.error('Erro ao criar categoria:', error);
      showAlert({
        title: 'Erro',
        message: error.response?.data?.detail || 'Erro ao criar categoria',
        variant: 'danger',
      });
    } finally {
      setCreatingCategory(false);
    }
  };

  const handleDeleteCategory = async (categoryId: number, categoryName: string) => {
    const ok = await showConfirm({
      title: 'Excluir categoria',
      message: `Tem certeza que deseja excluir a categoria "${categoryName}"?`,
      variant: 'danger',
      confirmLabel: 'Excluir',
      cancelLabel: 'Cancelar',
    });
    if (!ok) return;

    try {
      await knowledgeBaseApi.deleteCategory(categoryId);
      showAlert({ title: 'Sucesso', message: 'Categoria excluída com sucesso', variant: 'success' });
      fetchCategories();
      // Se a categoria excluída estava selecionada, limpar o filtro
      if (selectedCategory === categoryId) {
        setSelectedCategory('');
      }
    } catch (error: any) {
      console.error('Erro ao excluir categoria:', error);
      showAlert({
        title: 'Erro',
        message: error.response?.data?.detail || 'Erro ao excluir categoria',
        variant: 'danger',
      });
    }
  };

  const handleExportCategories = async () => {
    try {
      const payload = await knowledgeBaseApi.exportCategories();
      const jsonContent = JSON.stringify(payload, null, 2);
      const blob = new Blob([jsonContent], { type: 'application/json;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);

      link.setAttribute('href', url);
      link.setAttribute('download', `categorias_kb_export_${new Date().toISOString().split('T')[0]}.json`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erro ao exportar categorias:', error);
      showAlert({
        title: 'Erro',
        message: 'Erro ao exportar categorias.',
        variant: 'danger',
      });
    }
  };

  return (
    <div className="dashboard-page">
      <div className="dashboard-hero mb-3">
        <div className="d-flex justify-content-between align-items-center dashboard-header">
          <div>
            <h2 className="dashboard-title mb-1">Base de Conhecimento</h2>
            <small className="text-muted">
              Artigos, busca inteligente e assistente para orientação com base na documentação cadastrada.
            </small>
          </div>
          <div className="d-flex gap-2 flex-wrap justify-content-end">
            {(user?.role === 'admin' || user?.role === 'analista') && (
              <Button variant="outline-secondary" size="sm" onClick={() => setShowCategoriesModal(true)}>
                Categorias
              </Button>
            )}
            {user?.role === 'admin' && (
              <>
                <Button variant="outline-secondary" size="sm" onClick={() => setShowGlobalEditorsModal(true)}>
                  Editores globais
                </Button>
                <Button
                  variant="outline-warning"
                  size="sm"
                  onClick={() => {
                    setCorrelateResult(null);
                    setShowCorrelateModal(true);
                  }}
                >
                  Correlacionar autores
                </Button>
              </>
            )}
            <Button variant="outline-primary" size="sm" onClick={() => navigate('/knowledge-base/import')}>
              Importar artigo
            </Button>
            <Button variant="success" size="sm" onClick={() => navigate('/knowledge-base/new')}>
              + Criar artigo
            </Button>
          </div>
        </div>
      </div>

      {showKbAssistant && (
      <Card className="mb-3 dashboard-section-card kb-assist-card">
        <Card.Header className="d-flex align-items-center gap-2 py-2">
          <i className="bi bi-chat-dots text-primary" aria-hidden />
          <span>Assistente da base</span>
        </Card.Header>
        <Card.Body>
          <Form onSubmit={handleAskKnowledgeBase}>
            <Form.Group className="mb-3">
              <Form.Label className="dashboard-filter-label">Descreva sua dúvida ou o que precisa fazer</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                value={askQuestion}
                onChange={(e) => setAskQuestion(e.target.value)}
                onKeyDown={handleAskTextareaKeyDown}
                placeholder="Ex.: Como solicito acesso ao sistema X?"
                disabled={askLoading}
                aria-label="Pergunta ao assistente da base de conhecimento"
              />
              <Form.Text className="text-muted small d-block mt-2">
                <kbd className="kb-hint-kbd">Enter</kbd> envia a pergunta · <kbd className="kb-hint-kbd">Shift</kbd> +{' '}
                <kbd className="kb-hint-kbd">Enter</kbd> nova linha
              </Form.Text>
            </Form.Group>
            <Button type="submit" variant="primary" disabled={askLoading}>
              {askLoading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" />
                  Gerando resposta...
                </>
              ) : (
                <>
                  <i className="bi bi-lightbulb me-2" aria-hidden />
                  Obter orientação
                </>
              )}
            </Button>
          </Form>
          {askAnswer !== null && (
            <div className="mt-4">
              <div className="kb-answer-heading">
                <span className="kb-answer-heading__label">Resposta</span>
              </div>
              <div className="kb-answer-well mb-0">
                <KbAssistantMarkdown text={askAnswer} />
              </div>
              {askReferences.length > 0 && (
                <div className="kb-ref-list">
                  <div className="kb-answer-heading">
                    <span className="kb-answer-heading__label">Artigos relacionados</span>
                  </div>
                  <ul className="mb-0 ps-3">
                    {askReferences.map((ref) => (
                      <li key={ref.article_id}>
                        <Link to={ref.path}>{ref.title}</Link>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </Card.Body>
      </Card>
      )}

      <Card className="mb-3 dashboard-section-card">
        <Card.Header className="py-2">
          <span className="fw-semibold">Busca e filtros</span>
        </Card.Header>
        <Card.Body>
          <Row className="g-3 align-items-end">
            <Col md={6}>
              <Form.Label className="dashboard-filter-label mb-1">Texto</Form.Label>
              <InputGroup>
                <InputGroup.Text className="bg-transparent">
                  <i className="bi bi-search" aria-hidden />
                </InputGroup.Text>
                <Form.Control
                  size="sm"
                  type="text"
                  placeholder="Título ou texto; para ID do artigo use #número"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                />
              </InputGroup>
            </Col>
            <Col md={4}>
              <Form.Label className="dashboard-filter-label mb-1">Categoria</Form.Label>
              <Form.Select
                size="sm"
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value ? Number(e.target.value) : '')}
              >
                <option value="">Todas as categorias</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Check
                type="switch"
                id="favorites-only"
                className="mb-0"
                label="Só favoritos"
                checked={favoritesOnly}
                onChange={(e) => setFavoritesOnly(e.target.checked)}
              />
            </Col>
          </Row>
          <Row className="g-3 align-items-end mt-1">
            <Col md={4}>
              <Form.Label className="dashboard-filter-label mb-1">Autor</Form.Label>
              <Form.Control
                size="sm"
                list="kb-author-options"
                type="text"
                placeholder="Filtrar por autor..."
                value={authorFilter}
                onChange={(e) => setAuthorFilter(e.target.value)}
              />
              <datalist id="kb-author-options">
                {authorOptions.map((name) => (
                  <option key={name} value={name} />
                ))}
              </datalist>
            </Col>
            <Col md={3}>
              <Form.Label className="dashboard-filter-label mb-1">Ordenar por</Form.Label>
              <Form.Select
                size="sm"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as 'updated_at' | 'created_at' | 'title' | 'author')}
              >
                <option value="updated_at">Última atualização</option>
                <option value="created_at">Data de criação</option>
                <option value="author">Autor</option>
                <option value="title">Título</option>
              </Form.Select>
            </Col>
            <Col md={3}>
              <Form.Label className="dashboard-filter-label mb-1">Ordem</Form.Label>
              <Form.Select
                size="sm"
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value as 'asc' | 'desc')}
              >
                <option value="desc">Decrescente</option>
                <option value="asc">Crescente</option>
              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Label className="dashboard-filter-label mb-1">Por página</Form.Label>
              <Form.Select size="sm" value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}>
                <option value={12}>12</option>
                <option value={24}>24</option>
                <option value={48}>48</option>
                <option value={96}>96</option>
              </Form.Select>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {loading ? (
        <Card className="dashboard-section-card">
          <Card.Body className="text-center py-5">
            <Spinner animation="border" role="status" variant="primary" aria-hidden />
            <span className="visually-hidden">Carregando artigos</span>
            <p className="text-muted small mb-0 mt-3">Carregando artigos…</p>
          </Card.Body>
        </Card>
      ) : articles.length === 0 ? (
        <Card className="dashboard-section-card">
          <Card.Body className="text-center py-5 px-3">
            <i className="bi bi-journal-text fs-1 text-muted d-block mb-3" aria-hidden />
            <p className="text-muted mb-3">Nenhum artigo encontrado com os filtros atuais.</p>
            <Button variant="success" size="sm" onClick={() => navigate('/knowledge-base/new')}>
              + Criar primeiro artigo
            </Button>
          </Card.Body>
        </Card>
      ) : (
        <>
          <Row>
            {articles.map((article) => (
              <Col key={article.id} md={6} lg={4} className="mb-4">
                <Card className="h-100 dashboard-section-card kb-article-card">
                  <Card.Body className="d-flex flex-column">
                    <div className="d-flex justify-content-between align-items-start mb-2 gap-2">
                      <Badge bg="secondary" className="text-truncate">
                        {article.category?.name || 'Sem categoria'}
                      </Badge>
                      <Button
                        variant="link"
                        size="sm"
                        onClick={() => handleToggleFavorite(article.id)}
                        className={`kb-fav-btn p-0 ${article.is_favorite ? 'is-fav' : ''}`}
                        title={article.is_favorite ? 'Remover dos favoritos' : 'Adicionar aos favoritos'}
                        aria-label={article.is_favorite ? 'Remover dos favoritos' : 'Adicionar aos favoritos'}
                      >
                        <i className={article.is_favorite ? 'bi bi-star-fill' : 'bi bi-star'} aria-hidden />
                      </Button>
                    </div>
                    <Card.Title
                      as="h3"
                      className="mb-2 h6"
                      role="button"
                      tabIndex={0}
                      onClick={() => navigate(`/knowledge-base/${article.id}`)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          navigate(`/knowledge-base/${article.id}`);
                        }
                      }}
                    >
                      {article.title}
                    </Card.Title>
                    <Card.Text className="text-muted small flex-grow-1">{truncateText(article.body)}</Card.Text>
                    <div className="mt-2">
                      <small className="text-muted">
                        Por {article.author_name || article.author?.name || 'Desconhecido'} • {formatDate(article.updated_at)}
                      </small>
                    </div>
                  </Card.Body>
                  <Card.Footer className="py-2">
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => navigate(`/knowledge-base/${article.id}`)}
                      className="w-100"
                    >
                      Ler artigo
                      <i className="bi bi-arrow-right-short ms-1 fs-5 align-middle" aria-hidden />
                    </Button>
                  </Card.Footer>
                </Card>
              </Col>
            ))}
          </Row>
          <div className="kb-pagination-bar d-flex flex-column flex-sm-row align-items-stretch align-items-sm-center justify-content-between gap-3">
            <small className="text-muted mb-0">
              Mostrando {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, totalCount)} de {totalCount} artigo(s)
            </small>
            <div className="d-flex align-items-center justify-content-center gap-2 flex-wrap">
              <Button variant="outline-secondary" size="sm" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                <i className="bi bi-chevron-left me-1" aria-hidden />
                Anterior
              </Button>
              <span className="small text-muted px-1">
                Página {page} / {Math.max(1, Math.ceil(totalCount / pageSize))}
              </span>
              <Button
                variant="outline-secondary"
                size="sm"
                disabled={page >= Math.max(1, Math.ceil(totalCount / pageSize))}
                onClick={() => setPage((p) => p + 1)}
              >
                Próxima
                <i className="bi bi-chevron-right ms-1" aria-hidden />
              </Button>
            </div>
          </div>
        </>
      )}

      {/* Modal de Gerenciamento de Categorias */}
      <Modal show={showCategoriesModal} onHide={() => setShowCategoriesModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Gerenciar Categorias</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="d-flex gap-2 justify-content-end mb-3">
            <Button variant="outline-primary" onClick={handleExportCategories}>
              Exportar Categorias
            </Button>
            <Button variant="outline-secondary" onClick={() => setShowImportCategoriesModal(true)}>
              Importar Categorias
            </Button>
          </div>

          <Card className="mb-4">
            <Card.Header>
              <h5 className="mb-0">Criar Nova Categoria</h5>
            </Card.Header>
            <Card.Body>
              <Form onSubmit={handleCreateCategory}>
                <Form.Group className="mb-3">
                  <Form.Label>Nome da Categoria *</Form.Label>
                  <Form.Control
                    type="text"
                    value={newCategoryName}
                    onChange={(e) => setNewCategoryName(e.target.value)}
                    placeholder="Digite o nome da categoria"
                    required
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Descrição</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={newCategoryDescription}
                    onChange={(e) => setNewCategoryDescription(e.target.value)}
                    placeholder="Digite uma descrição (opcional)"
                  />
                </Form.Group>
                <Button variant="primary" type="submit" disabled={creatingCategory}>
                  {creatingCategory ? 'Criando...' : 'Criar Categoria'}
                </Button>
              </Form>
            </Card.Body>
          </Card>

          <Card>
            <Card.Header>
              <h5 className="mb-0">Categorias Existentes</h5>
            </Card.Header>
            <Card.Body>
              {categories.length === 0 ? (
                <p className="text-muted text-center py-3">Nenhuma categoria cadastrada.</p>
              ) : (
                <Table striped bordered hover>
                  <thead>
                    <tr>
                      <th>Nome</th>
                      <th>Descrição</th>
                      <th style={{ width: '100px' }}>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {categories.map((category) => (
                      <tr key={category.id}>
                        <td>{category.name}</td>
                        <td>{category.description || <span className="text-muted">-</span>}</td>
                        <td>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleDeleteCategory(category.id, category.name)}
                            title="Excluir categoria"
                          >
                            🗑️
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCategoriesModal(false)}>
            Fechar
          </Button>
        </Modal.Footer>
      </Modal>

      <ImportCategoriesModal
        show={showImportCategoriesModal}
        onHide={() => setShowImportCategoriesModal(false)}
        onSuccess={fetchCategories}
      />

      <Modal show={showGlobalEditorsModal} onHide={() => setShowGlobalEditorsModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Editores globais — Base de conhecimento</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted small">
            Usuários listados abaixo podem <strong>editar e excluir qualquer artigo</strong> da base de conhecimento
            (além de administradores e donos).
          </p>
          {loadingGlobalEditors ? (
            <div className="text-center py-3">
              <Spinner animation="border" />
            </div>
          ) : (
            <Table striped bordered hover size="sm" className="mb-4">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Email</th>
                  <th>Desde</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {globalEditors.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="text-center text-muted">
                      Nenhum editor global configurado.
                    </td>
                  </tr>
                ) : (
                  globalEditors.map((ed) => (
                    <tr key={ed.user_id}>
                      <td>{ed.name}</td>
                      <td>{ed.email}</td>
                      <td>{new Date(ed.granted_at).toLocaleString('pt-BR')}</td>
                      <td>
                        <Button size="sm" variant="outline-danger" onClick={() => handleRevokeGlobalEditor(ed.user_id)}>
                          Revogar
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          )}
          <h6 className="mb-2">Adicionar usuário</h6>
          <Row className="g-2 align-items-end mb-2">
            <Col md={8}>
              <Form.Label className="small text-muted">Buscar por nome ou e-mail</Form.Label>
              <Form.Control
                value={globalUserSearch}
                onChange={(e) => setGlobalUserSearch(e.target.value)}
                placeholder="Nome ou e-mail"
              />
            </Col>
            <Col md={4}>
              <Button
                variant="primary"
                className="w-100"
                onClick={handleSearchGlobalUsers}
                disabled={globalUserSearchLoading}
              >
                {globalUserSearchLoading ? <Spinner size="sm" animation="border" /> : 'Buscar'}
              </Button>
            </Col>
          </Row>
          {globalUserResults.length > 0 && (
            <Table size="sm" bordered>
              <tbody>
                {globalUserResults.map((u) => (
                  <tr key={u.id}>
                    <td>
                      {u.name} <span className="text-muted small">({u.email})</span>
                    </td>
                    <td className="text-end" style={{ width: 120 }}>
                      <Button size="sm" variant="success" onClick={() => handleGrantGlobalEditor(u.id)}>
                        Conceder
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowGlobalEditorsModal(false)}>
            Fechar
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showCorrelateModal} onHide={() => setShowCorrelateModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Correlacionar autores importados</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted small">
            Compara o campo <strong>Autor</strong> importado (author_name) com os nomes de usuários ativos. Em seguida
            pode aplicar a troca do dono do artigo (<code>created_by</code>) apenas quando a confiança for alta.
          </p>
          <div className="d-flex flex-wrap gap-2 mb-3">
            <Button variant="outline-primary" onClick={handleCorrelatePreview} disabled={correlateLoading}>
              {correlateLoading ? <Spinner size="sm" animation="border" /> : 'Pré-visualizar'}
            </Button>
            <Button variant="warning" onClick={handleCorrelateApply} disabled={correlateLoading}>
              Aplicar alta confiança
            </Button>
          </div>
          {correlateResult && (
            <>
              <Alert variant="info" className="mb-2 py-2 small">
                Artigos analisados: {correlateResult.articles_examined} | Alta confiança:{' '}
                {correlateResult.high_confidence.length} | Revisar: {correlateResult.needs_review.length} | Sem match:{' '}
                {correlateResult.not_found.length}
                {correlateResult.apply ? (
                  <>
                    {' '}
                    | <strong>Atualizados: {correlateResult.updated_count}</strong>
                  </>
                ) : null}
              </Alert>
              {correlateResult.needs_review.length > 0 && (
                <div className="mb-3" style={{ maxHeight: 200, overflow: 'auto' }}>
                  <strong className="small">Precisam revisão manual ({correlateResult.needs_review.length})</strong>
                  <Table size="sm" bordered className="mb-0 mt-1">
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Título</th>
                        <th>Autor importado</th>
                        <th>Sugestão</th>
                        <th>Score</th>
                      </tr>
                    </thead>
                    <tbody>
                      {correlateResult.needs_review.map((r) => (
                        <tr key={r.article_id}>
                          <td>{r.article_id}</td>
                          <td>{r.title}</td>
                          <td>{r.author_name}</td>
                          <td>
                            {r.matched_user_name} (#{r.matched_user_id})
                          </td>
                          <td>{r.score}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCorrelateModal(false)}>
            Fechar
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

const ImportCategoriesModal: React.FC<{
  show: boolean;
  onHide: () => void;
  onSuccess: () => void;
}> = ({ show, onHide, onSuccess }) => {
  const [file, setFile] = useState<File | null>(null);
  const [dryRun, setDryRun] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [result, setResult] = useState<KnowledgeCategoriesImportResponse | null>(null);

  const handleClose = () => {
    setFile(null);
    setDryRun(true);
    setLoading(false);
    setError('');
    setSuccess('');
    setResult(null);
    onHide();
  };

  const handleImport = async () => {
    if (!file) {
      setError('Selecione um arquivo JSON.');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');
    setResult(null);

    try {
      const text = await file.text();
      const payload = JSON.parse(text) as KnowledgeCategoriesImportPayload;
      if (!payload || !Array.isArray(payload.categories)) {
        throw new Error('Arquivo inválido: esperado objeto com campo "categories".');
      }

      const response = await knowledgeBaseApi.importCategories(payload, dryRun);
      setResult(response);

      if (response.errors > 0) {
        setError(`Importação finalizada com ${response.errors} erro(s).`);
      }

      setSuccess(
        dryRun
          ? `Validação concluída: ${response.created} criação(ões), ${response.updated} atualização(ões).`
          : `Importação concluída: ${response.created} criada(s), ${response.updated} atualizada(s).`
      );

      if (!dryRun) {
        onSuccess();
      }
    } catch (err: any) {
      console.error('Erro ao importar categorias:', err);
      setError(err.response?.data?.detail || err.message || 'Falha ao importar categorias.');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadTemplate = () => {
    const template: KnowledgeCategoriesImportPayload = {
      version: '1.0',
      categories: [
        {
          name: 'Categoria Exemplo',
          description: 'Descrição opcional',
        },
      ],
    };

    const jsonContent = JSON.stringify(template, null, 2);
    const blob = new Blob([jsonContent], { type: 'application/json;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', 'template_importacao_categorias_kb.json');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Importar Categorias</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group className="mb-3">
          <Form.Label>Arquivo JSON</Form.Label>
          <Form.Control
            type="file"
            accept=".json"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFile(e.target.files?.[0] || null)}
            disabled={loading}
          />
          <Form.Text className="text-muted">
            Utilize o arquivo gerado por "Exportar Categorias".
          </Form.Text>
        </Form.Group>
        <div className="mb-3">
          <Button variant="outline-info" size="sm" onClick={handleDownloadTemplate} disabled={loading}>
            Baixar Template JSON
          </Button>
        </div>
        <Form.Check
          type="switch"
          id="knowledge-categories-dry-run"
          label="Executar validação sem gravar (dry_run)"
          checked={dryRun}
          onChange={(e) => setDryRun(e.target.checked)}
          disabled={loading}
          className="mb-3"
        />

        {error && <Alert variant="danger">{error}</Alert>}
        {success && <Alert variant="success">{success}</Alert>}

        {result && (
          <div style={{ maxHeight: 220, overflow: 'auto' }}>
            <Table size="sm" bordered>
              <thead>
                <tr>
                  <th>Categoria</th>
                  <th>Ação</th>
                  <th>Mensagem</th>
                </tr>
              </thead>
              <tbody>
                {result.results.map((item, idx) => (
                  <tr key={`${item.name}-${idx}`}>
                    <td>{item.name}</td>
                    <td>{item.action}</td>
                    <td>{item.message || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={loading}>
          Fechar
        </Button>
        <Button variant="primary" onClick={handleImport} disabled={!file || loading}>
          {loading ? 'Importando...' : dryRun ? 'Validar' : 'Importar'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default KnowledgeBase;
